import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      this.restartEntranceAnimations(details);

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      onAnimationEnd(drawer || details, () => trapFocus(details), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        // SOMENIUM: si el cliente ha vuelto a abrir el menú mientras se estaba
        // cerrando, aquí NO hay que cerrar nada.
        //
        // Al reabrir, la transición de cierre del panel se sustituye por la de
        // apertura y la anterior queda cancelada. `onAnimationEnd` usa
        // `Promise.allSettled`, que también resuelve con las canceladas, así que
        // esta devolución de llamada se ejecutaba igualmente y `reset()` cerraba
        // el menú de golpe delante del cliente.
        if (details.classList.contains('menu-open')) return;

        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * SOMENIUM: reinicia la animación de entrada de los enlaces en CADA apertura.
   *
   * La animación la dispara el CSS con `.menu-drawer-container[open]`, así que
   * sin JavaScript sigue funcionando. El problema es que el atributo `open` solo
   * se quita al TERMINAR el cierre: si el cliente cierra y vuelve a abrir antes
   * de que acabe, `open` nunca llegó a desaparecer, el navegador no ve ningún
   * cambio y no vuelve a lanzar la animación → el menú se abría con los enlaces
   * ya puestos, sin entrada.
   *
   * Se corta la animación y se devuelve al CSS, forzando un reflujo en medio.
   * Así el navegador la crea DE NUEVO y se ve entera, se haya recargado la
   * página o no.
   *
   * No sirve rebobinar con `getAnimations()`: en cuanto la animación termina, el
   * navegador la destruye (no deja rastro al acabar) y no queda nada que
   * rebobinar. Justo el caso que hay que arreglar.
   *
   * @param {HTMLDetailsElement} details - El menú que se acaba de abrir
   */
  restartEntranceAnimations(details) {
    const elements = details.querySelectorAll('.menu-drawer__animated-element, .menu-drawer__close-button');

    if (!elements.length) return;

    // 1. Cortamos. Va con `important` porque la regla del CSS también lo lleva:
    //    sin eso el estilo en línea perdería y no se cortaría nada.
    for (const element of elements) element.style.setProperty('animation', 'none', 'important');

    // 2. Un único reflujo forzado para todo el menú: el navegador da por buena
    //    la interrupción. Con uno basta, no hace falta uno por enlace.
    void details.offsetWidth;

    // 3. Devolvemos el control al CSS: la regla vuelve a aplicarse desde cero.
    for (const element of elements) element.style.removeProperty('animation');
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
